<script setup lang="ts">
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { capitalizeFirstLetter } from '@/utils/text';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useDisplay } from 'vuetify';

const globalStore = useGlobalStore();
const authStore = useAuthStore();

const props = defineProps<{
    routeName: string;
    module: string;
}>();

const { smAndDown, mdAndUp, lgAndUp } = useDisplay();

const isScrolled = ref(false);

function getPageTitleWidth() {
    if (!authStore.isAuthenticated) return '100vw';

    if (lgAndUp.value) return 'calc(100vw - 215px)';
    if (mdAndUp.value) return 'calc(100vw - 50px)';
    return '100vw';
}

const pageTitleStyle = computed(() => ({
    width: getPageTitleWidth()
}));

const pageTitle = computed(() => {
    if (props.module) return `Module: ${capitalizeFirstLetter(props.module)}`;

    return capitalizeFirstLetter(globalStore.pageTitle ?? '');
});

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

function handleScroll() {
    const hasOverlay = document.querySelector('html')?.classList.contains('v-overlay-scroll-blocked') ?? true;
    isScrolled.value = window.scrollY > 50 || hasOverlay;
}
</script>

<template>
    <div class="sticky-header w-100">
        <div>
            <v-expand-transition>
                <div v-if="isScrolled && !smAndDown" class="scrolled">
                    <h1 class="page-title page-title-scrolled" :style="pageTitleStyle">{{ pageTitle }}</h1>
                </div>
                <div v-else>
                    <object class="wavy-header" data="/images/header.svg" height="190"></object>
                    <h1 v-if="!smAndDown" class="page-title" :style="pageTitleStyle">{{ pageTitle }}</h1>

                    <v-app-bar sticky elevation="1" v-else class="bg-main-dark">
                        <v-app-bar-title class="text-white text-center text-truncate" style="max-width: 70vw; margin-left: 15vw">
                            {{ pageTitle }}
                        </v-app-bar-title>
                    </v-app-bar>
                </div>
            </v-expand-transition>
            <NotificationBell v-if="authStore.isAuthenticated" />
        </div>
    </div>
</template>

<style scoped>
.sticky-header {
    position: fixed;
    z-index: 99;
    top: 0;
}

.page-title-scrolled {
    margin-top: 0em;
}

.wavy-header {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.scrolled {
    @media screen and (min-width: 768px) {
        height: 5em;
        background-color: var(--c-main-dark);
    }
}

/* Set custom transition duration for v-expand-transition */
.expand-transition-active,
.expand-transition-enter-active {
    transition: all 0.5s ease-in-out !important; /* Change 0.5s to your desired duration */
}

.expand-transition-enter-to,
.expand-transition-leave-to {
    transform: scale(1);
}

@media screen and (max-width: 1282px) {
    .wavy-header {
        height: 200px;
    }
}

@media screen and (max-width: 840px) {
    .wavy-header {
        display: none;
    }
}
</style>
